import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { cloneDeep } from 'lodash-es';
import { getPreviousPagePath, goBack, getRoute } from '../routing';
import { hrefWithBasename } from '../helpers';
import { setData as setDataAction } from '../actions';
import {
  REVIEW_EDIT_EVENTS,
  finishReviewEditTrip as finishReviewEditTripAction,
  getReviewReturnLocation,
  isReviewEditTrip,
  peekReviewEditBaseline,
  recordReviewEditEvent,
  setReviewEditReturnFocus,
  takeReviewEditBaseline,
} from '../review/reviewEditTrip';

/**
 * Equivalent to back button on the back/continue pair,
 * but a link instead of a button
 *
 * Usage:
 * ```jsx
 * <BackLink />
 * ```
 *
 * BackLink vs BackLinkImpl
 * BackLink is what you should use as a consumer
 * BackLinkImpl is for testing so we can pass in props directly
 */
export const BackLinkImpl = ({
  text = 'Back to previous page',
  router, // from withRouter
  routes, // from withRouter
  location, // from withRouter
  form, // from connect
  setData, // from connect
  finishReviewEditTrip, // from connect
  featureToggles, // from connect
  dynamicPaths = false,
}) => {
  const [href, setHref] = useState(null);

  useEffect(() => {
    if (!routes || !form.data || !location.pathname) {
      return;
    }
    try {
      let newHref;
      const route = getRoute(routes, location, dynamicPaths);

      if (isReviewEditTrip(route?.formConfig, featureToggles, location)) {
        newHref = getReviewReturnLocation(
          route.pageList,
          route.formConfig?.urlPrefix || '',
          '',
        ).pathname;
      } else if (typeof route.pageConfig?.onNavBack === 'function') {
        // if onNavBack is defined, then the consumer is doing
        // something custom and we can't determine the newHref,
        // possibly including side effects like setting data
        newHref = '#';
      } else {
        newHref = getPreviousPagePath(
          route.pageList,
          form.data,
          location.pathname,
        );
      }

      // '#' is a placeholder, not a path
      setHref(newHref === '#' ? newHref : hrefWithBasename(router, newHref));
    } catch (e) {
      // possible if we're already on first page
      setHref(null);
    }
  }, [location, form.data, routes, form, setData, router, featureToggles]);

  function onClick(e) {
    e.preventDefault();

    const route = getRoute(routes, location, dynamicPaths);

    if (isReviewEditTrip(route?.formConfig, featureToggles, location)) {
      const formId = route?.formConfig?.formId || form?.formId;
      const baseline = peekReviewEditBaseline(formId);
      if (baseline) {
        setData(cloneDeep(baseline));
      }
      const pageKey =
        form?.reviewEditTrip?.originPageKey ||
        `${route.pageConfig?.pageKey || ''}${router.params?.index ?? ''}`;
      setReviewEditReturnFocus(pageKey);
      takeReviewEditBaseline(formId);
      recordReviewEditEvent(
        form?.trackingPrefix || route?.formConfig?.trackingPrefix || '',
        REVIEW_EDIT_EVENTS.CANCEL,
      );
      finishReviewEditTrip?.();
      router.push(
        getReviewReturnLocation(
          route.pageList,
          route.formConfig?.urlPrefix || '',
          pageKey,
        ),
      );
      return;
    }

    goBack({
      formData: form.data,
      index: router.params?.index ? Number(router.params.index) : undefined,
      location,
      onNavBack: route?.pageConfig?.onNavBack,
      pageList: route?.pageList,
      router,
      setData,
      urlPrefix: route?.formConfig?.urlPrefix || '',
    });
  }

  if (!href) {
    return null;
  }

  return (
    <nav
      className="vads-u-margin-top--2 vads-u-margin-bottom--4 vads-u-font-size--sans-sm vads-u-display--inline-block"
      aria-label="Previous page"
    >
      <va-link back href={href} text={text} onClick={onClick} />
    </nav>
  );
};

function mapStateToProps(state) {
  return {
    form: state.form,
    featureToggles: state.featureToggles || {},
  };
}

const mapDispatchToProps = {
  setData: setDataAction,
  finishReviewEditTrip: finishReviewEditTripAction,
};

BackLinkImpl.propTypes = {
  dynamicPaths: PropTypes.bool,
  featureToggles: PropTypes.object,
  finishReviewEditTrip: PropTypes.func,
  form: PropTypes.object, // from connect
  location: PropTypes.object, // from withRouter
  router: PropTypes.object, // from withRouter
  routes: PropTypes.array, // from withRouter
  setData: PropTypes.func, // from connect
  text: PropTypes.string,
};

const BackLink = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(BackLinkImpl),
);

export default BackLink;
